import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {isMobile} from 'react-device-detect';
import {
   events,
   ISmartPlayerGACustomEvent,
   ISmartPlayerRumDataEvent,
   ISmartPlayerTimeUpdateEvent,
   log,
   mediaInterface,
   mediaInterfaceConstants,
   preloadOptions
} from '@techsmith/techsmith-smartplayer';
import {StringParam, useQueryParam} from 'use-query-params';
import SmartPlayerConstants from '../../constants/smartPlayerConstants';
import Analytics from '../../service/analytics';
import config from '../../service/config';
import logging from '../../service/logging';
import MediaApi from '../../service/mediaApi';
import resumablePlayback from '../../service/resumablePlayback';
import StringUtil from '../../util/StringUtil';
import '@techsmith/techsmith-smartplayer/dist/techsmith-smart-player.css';
import '../../static/css/modal.less';
import Constants, {AnnotationModes, EmbedPlatforms, EntryPoint} from '../../constants/Constants';
import CommentMarkers from '../conversation/CommentMarkers';
import SnagitMarketingEmbedPlugin from '../marketing/SnagitMarketingEmbedPlugin';
import {showEmbeddedSnagitMarketing} from '../../util/MarketingHelper';
import ReactDOM from 'react-dom';
import Annotation from '../conversation/Annotation';
import {TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {IComment} from '../../model/commentModel';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {updateDisableConversationState} from '../../context/disableConversationProvider';
import TimeFormatter from '../../util/TimeFormatter';
import {useUserProfileData} from '../../context/userProfileProvider';
import {commentsStore} from '../../context/commentsProvider';
import {themeStore} from '../../context/themeProvider';
import {updateQuizData} from '../../context/quizDataProvider';
import {mediaStore, updateMediaStateFromServer} from '../../context/mediaProvider';
import cssConstants from '../../constants/cssConstants';
import ReactionsPlugin from '../reactions/ReactionsPlugin';
import ReactionsPlaybackPlugin from '../reactions/ReactionsPlaybackPlugin';
import {EnvironmentType} from '../../model/appModel';
import CaptionDisplayStatusPlugin from './CaptionDisplayStatusPlugin';
import {CaptioningTypes} from '../../model/captionModels';
import {IMediaChapters} from '../../model/mediaDetails';

const smartPlayerApp = document.createElement('div');
smartPlayerApp.id = 'tscMediaContent';
smartPlayerApp.style.margin = 'auto';
let started = false;
let lastSavedTime = 0;
const timeBuffer = 3;

// eslint-disable-next-line no-console
console.info(`%cUsing TechSmith Smart Player version: ${mediaInterface.version}`, 'color:blue; font-size:18px');

const SmartPlayerWrapper = styled.div<{isEmbedded: boolean}>`
   display: flex;
   justify-content: center;
   width: ${props => props.isEmbedded ? '100vw' : '100%'};
   height: ${props => props.isEmbedded ? '100vh' : '100%'};
   min-height: 0; // Safe help for smartplayer to not blowout the media-base container grid

   .annotation-wrapper {
      height: 100%;
      overflow: hidden;
   }

   .reactions-playback-wrapper {
      height: 100%;
      overflow: hidden;
   }
`;

const SmartPlayerContainerDiv = styled.div<{annotationMode: AnnotationModes; isEmbedView: boolean; isImage: boolean; isYouTube: boolean; isQuizUserSelfIdentifyOpen: boolean}>`
   width: 100%;

   .tsc-smartplayer {
      color: #fff;

      .video-wrapper {
         ${props => props.isQuizUserSelfIdentifyOpen ? `width: ${cssConstants.smartPlayerQuizSetupWidth}` : ''};
         ${props => props.isQuizUserSelfIdentifyOpen ? `height: ${cssConstants.smartPlayerQuizSetupHeight}` : ''};
      }

      .display-image-quiz-button, .question-set-container {
         position: absolute !important;
      }

      video {
         background-color: #000;
         color: #fff;
      }

      /* this is needed so the SP controls dont beat out the media tabs on narrow-tall media in */
      /* iOS safari which didn't seem to be respecting calculated height properly, before final calculations */
      /* i saw evidence of this behavior in chrome as well, so set for all mobile just to be sure */
      ${isMobile ? `.video-controls-container {
         z-index: auto !important;

         .progress-bar-control {
            z-index: auto !important;
         }
      }` : ''}

      #controls, .video-controls-container {
         visibility: ${props => props.annotationMode !== AnnotationModes.edit ? 'visible' : 'hidden'};
      }

      ${props => !props.isEmbedView && !props.isImage ? `min-width: ${cssConstants.smartPlayerMinWidth};` : ''} /* smartplayer itself needs min width to show usable control-bar for non-images, we should not need to use smartPlayerMinWidth elsewhere */
   }
`;

export const SmartPlayerBase: React.FC<ISmartPlayerProps & IStateMappedProps> = ({isEmbedded, embedPlatform, onDisplayModeChange, annotationMode, SelectedComment, comments, theme, ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters}) => {
   const [smartPlayerTimeQueryString] = useQueryParam(Constants.queryParams.smartPlayerStartTime, StringParam);
   const [smartPlayerTimeQueryStringUppercase] = useQueryParam(Constants.queryParams.smartPlayerStartTimeUppercase, StringParam);
   const [commentId] = useQueryParam(Constants.queryParams.commentId, StringParam);
   const smartPlayerContainer = useRef<HTMLDivElement>(null);
   const [commentMarkerDomElement, setCommentMarkerDomElement] = useState(null);
   const [annotationDomElement, setAnnotationDomElement] = useState(null);
   const [reactionsDomElement, setReactionsDomElement] = useState(null);
   const [reactionsPlaybackDomElement, setReactionsPlaybackDomElement] = useState(null);
   const [snagitMarketingMediaDomElement, setSnagitMarketingMediaDomElement] = useState(null);
   const [captionDisplayStatusDomElement, setCaptionDisplayStatusDomElement] = useState(null);
   const [mediaDuration, setMediaDuration] = useState(0);
   const [showSnagitMarketing, setShowSnagitMarketing] = useState<boolean>(true);
   const [userHasHiddenSnagitMarketing, setUserHasHiddenSnagitMarketing] = useState<boolean>(false);
   const [quizUserSelfIdentifyIsOpen, setQuizUserSelfIdentifyIsOpen] = useState(false);
   const [smartPlayerMediaError, setSmartPlayerMediaError] = useState(false);
   const {isWorking: userProfileIsLoading, userProfile} = useUserProfileData();

   const conditionallyConfigurePlaybackResume = () => {
      if (smartPlayerTimeQueryStringUppercase || smartPlayerTimeQueryString || commentId) {
         return;
      }

      const savedTime = resumablePlayback.getProgress(config.user.TechSmithId, Hash);
      const isTimeSafeToSeek = savedTime + timeBuffer === mediaInterface.getSafeSeekTime(savedTime + timeBuffer);

      if (isTimeSafeToSeek) {
         mediaInterface.currentTime = savedTime;
      }
   };

   const recordGAViewEvent = () => {
      const gaViewEvent = {
         category: SmartPlayerConstants.analytics.viewEvent,
         action: '',
         label: Hash,
         value: 0
      };

      if (config.environmentData.IsLti) {
         gaViewEvent.action = SmartPlayerConstants.analytics.viewAction.FromLti;
      } else if (!window.document.referrer) {
         gaViewEvent.action = SmartPlayerConstants.analytics.viewAction.FromUnknown;
      } else if (RegExp(config.environmentData.GoogleAnalyticsDomain).exec(window.document.referrer) === null) {
         gaViewEvent.action = SmartPlayerConstants.analytics.viewAction.FromEmbed;
      } else {
         gaViewEvent.action = SmartPlayerConstants.analytics.viewAction.FromViewPage;
      }
      Analytics.gaEvent(gaViewEvent);
   };

   const videoStartEventListener = () => {
      recordGAViewEvent();
      void MediaApi.incrementView(Hash); // intentionally not-awaited
      if (!IsYouTube) {
         mediaInterface.controls = mediaInterfaceConstants.controls.autohide;
      }
      mediaInterface.removeEventListener(events.External.VideoPlay, videoStartEventListener);
   };

   const mediaReadyEventListener = () => {
      IsImage && MediaApi.incrementView(Hash);
      updateDisableConversationState({isAnnotatingDisabled: false, isCommentingDisabled: false});
   };

   const displayModeChangedEventListener = (displayMode: string) => {
      onDisplayModeChange && onDisplayModeChange(displayMode);
   };

   const addEventListeners = () => {
      if (log && log.addEventListener) {
         log.addEventListener('EXCEPTION', e => {
            logging.warn('Encountered exception in Smart Player', e);
         });
      }

      mediaInterface.addEventListener(events.External.ShowStartQuizPrompt, () => {
         setQuizUserSelfIdentifyIsOpen(true);
      });

      mediaInterface.addEventListener(events.External.HideStartQuizPrompt, () => {
         setQuizUserSelfIdentifyIsOpen(false);
      });

      mediaInterface.addEventListener(events.External.QuizVisible, () => {
         updateQuizData({IsQuizzing: true});
         if (!IsYouTube) {
            mediaInterface.controls = mediaInterfaceConstants.controls.true;
         }
      });

      mediaInterface.addEventListener(events.External.QuizHidden, () => {
         updateQuizData({IsQuizzing: false});
         if (!IsYouTube) {
            mediaInterface.controls = mediaInterfaceConstants.controls.autohide;
         }
      });

      mediaInterface.addEventListener(events.ExternalVideo.VideoLoadedData, () => {
         conditionallyConfigurePlaybackResume();
      });

      mediaInterface.addEventListener(events.External.VideoEnd, () => {
         updateDisableConversationState({isAnnotatingDisabled: true});
      });

      mediaInterface.addEventListener(events.External.VideoReplay, () => {
         updateDisableConversationState({isAnnotatingDisabled: false});
      });

      mediaInterface.addEventListener(events.ExternalVideo.VideoLoadedMetadata, () => {
         setMediaDuration(mediaInterface.duration);
      });

      mediaInterface.addEventListener(events.External.MediaError, () => {
         setSmartPlayerMediaError(true);
      });

      mediaInterface.addEventListener(events.External.PlayerControlsHidden, () => {
         setShowSnagitMarketing(false);
      });

      mediaInterface.addEventListener(events.External.PlayerControlsVisible, () => {
         setShowSnagitMarketing(true);
      });

      mediaInterface.addEventListener<ISmartPlayerTimeUpdateEvent>(events.ExternalVideo.VideoTimeUpdate, () => {
         const roundedTime = Math.floor(mediaInterface.currentTime);

         if (roundedTime !== lastSavedTime) {
            resumablePlayback.setProgress(config.user.TechSmithId, Hash, roundedTime.toString());
            lastSavedTime = roundedTime;
         }
      });

      mediaInterface.addEventListener(events.External.VideoPlay, videoStartEventListener);
      mediaInterface.addEventListener(events.External.MediaReady, mediaReadyEventListener);

      mediaInterface.addEventListener(events.External.DisplayModeChanged, displayModeChangedEventListener);

      mediaInterface.addEventListener<ISmartPlayerGACustomEvent>(events.External.GoogleAnalyticsCustomEvent, e => {
         // the events coming out of SP aren't reliable enough not to sanitize, what should be all strings (and number for value)
         // are coming out booleans, numbers, undefined or otherwise unpredictable and causing our our code to throw errors without this
         const sanitizedEvent: ISmartPlayerGACustomEvent = {
            category: JSON.stringify(e.category),
            action: JSON.stringify(e.action),
            label: JSON.stringify(e.label),
            value: e.value
         };
         Analytics.gaEvent(sanitizedEvent);

         Analytics.aiEvent({
            name: 'SmartPlayerEvent',
            label: sanitizedEvent.action,
            value: sanitizedEvent.label
         });
      });

      mediaInterface.addEventListener<ISmartPlayerRumDataEvent>(events.External.PlayerRumData, e => {
         const categorySuffix = e.categorySuffix || '';
         if (e?.rum) {
            e.rum.forEach(rumItem => {
               Analytics.gaMeasurement({
                  category: `SmartPlayerRUM_PresentationWebClient${categorySuffix}`,
                  variable: rumItem.name,
                  value: rumItem.time
               });

               Analytics.aiMeasurement({
                  name: `SmartPlayerTiming_PresentationWebClient${categorySuffix}`,
                  label: rumItem.name,
                  value: rumItem.time
               });
            });
         }
      });
   };

   const configure = () => {
      // the .replace is for older youtube content urls that may be non-secure protocol
      mediaInterface.addSrc(ContentUrl.replace(/http:\/\//i, 'https://'));
      if (HlsTokenInjectionProxyContentUrl) {
         mediaInterface.addSrc(HlsTokenInjectionProxyContentUrl);
      }
      mediaInterface.disableContinueWithoutQuestions = true;
      mediaInterface.posterSrc = PosterUrl;
      mediaInterface.tocSrc = SpriteUrl;
      mediaInterface.rememberUserInfo = true;

      if (!isEmbedded && config.featureSwitches.IsScreencast) {
         mediaInterface.enableTheaterMode = true;
      }

      mediaInterface.allowFastForward = AllowViewerToFastForwardVideo;

      if (!StringUtil.isNullOrWhiteSpace(XmpUrl)) {
         mediaInterface.xmpSrc = XmpUrl;
      }

      if (!StringUtil.isNullOrWhiteSpace(AudioDescriptionUrl)) {
         mediaInterface.audioDescriptionMediaSrc = AudioDescriptionUrl;
      }

      if (embedPlatform === EmbedPlatforms.slack) {
         mediaInterface.disablePictureInPicture = true;
      }

      mediaInterface.showADACompliantCaptions = !!config.featureSwitches.ForceADACompliantCaptionStyle;

      if (config.environmentData.Environment !== EnvironmentType.live) {
         mediaInterface.quizServiceBaseUrl = config.environmentData.QuizzingServiceUrl;
         mediaInterface.resultsServiceBaseUrl = config.environmentData.ResultsRootUrl;
      }

      mediaInterface.quizCreateInstanceUrl = config.environmentData.QuizzingServiceCreateInstanceUrl;

      if (!StringUtil.isNullOrWhiteSpace(QuizId)) {
         mediaInterface.quizId = QuizId;
      }

      if (Rotate) {
         mediaInterface.rotation = Rotate;
      }

      if (IsYouTube) {
         mediaInterface.controls = SmartPlayerConstants.youtubeControls;
      } else {
         if (PreviewThumbnailsUri) {
            mediaInterface.videoThumbnailsSrc = PreviewThumbnailsUri;
            mediaInterface.videoThumbnailWidth = PreviewThumbnailsThumbWidth;
            mediaInterface.videoThumbnailHeight = PreviewThumbnailsThumbHeight;
            mediaInterface.videoThumbnailInterval = PreviewThumbnailsInterval;
            mediaInterface.videoThumbnailColumns = PreviewThumbnailsMaxColumns;
         }

         mediaInterface.useLargerProgressBar = false;
         // the watch page must 'disable' the preload play button overlay for the sake of conversation interaction (also force controls on)
         if (config.entryPoint === EntryPoint.watch) {
            mediaInterface.preload = preloadOptions.metadata;
            mediaInterface.controls = mediaInterfaceConstants.controls.true;
         }

         if (isEmbedded) {
            mediaInterface.customFullScreenDomElement = document.getElementsByClassName(Constants.mainElementClassName)[0];
         }
      }

      if (IsVideo && Chapters || !StringUtil.isNullOrWhiteSpace(Description)) {
         if (Chapters) {
            const chaptersString = Chapters.chapters.map(chapter => `${TimeFormatter.formatTime(chapter.timeInMilliseconds / 1000)} ${chapter.label}`).join('\n');
            mediaInterface.createTocFromDescription(encodeURIComponent(chaptersString));
         } else {
            mediaInterface.createTocFromDescription(encodeURIComponent(Description));
         }
      }

      mediaInterface.background = IsImage ? theme === TscThemeName.dawn ? SmartPlayerConstants.imageBackgroundColorDawn : SmartPlayerConstants.imageBackgroundColorDusk : SmartPlayerConstants.videoBackgroundColor;

      mediaInterface.contextMenu = false;

      // need this or camtasia produced recordings that set unicodeenabled=true in xmp will break SAS tokens as they are set up in smartplayer (JMD: 2019-08)
      mediaInterface.ignoreXmpControllerParameters = ['unicodeenabled', 'autohide', 'backgroundcolor'];

      setUserInfo();

      mediaInterface.userSignedIn = config.user.IsSignedIn && (config.watchConfig?.isOwner || config.embedConfig?.isOwner || !config.featureSwitches.DisableSmartPlayerAccessToken);
   };

   const setUserInfo = () => {
      if (userProfile) {
         mediaInterface.userInfo = {
            firstName: userProfile.firstName || '',
            lastName: userProfile.lastName || '',
            email: config.user.EmailAddress || ''
         };
      }
   };

   const run = async () => {
      if (!started) {
         started = true;
         addEventListeners();
         mediaInterface.mediaTitle = Title;
         mediaInterface.run('tscMediaContent');

         if (mediaInterface.controls !== SmartPlayerConstants.youtubeControls) {
            if (config.featureSwitches.EnableReactions && IsVideo) {
               const reactionsPluginInfo = await mediaInterface.registerPlugin('techsmithReactions', Constants.pluginElements.progressBar);
               reactionsPluginInfo.pluginContainerElement.classList.add('reactions-track');
               setReactionsDomElement(reactionsPluginInfo.pluginContainerElement);

               const reactionsPlaybackPluginInfo = await mediaInterface.registerPlugin('techsmithReactionsPlayback', Constants.pluginElements.player);
               reactionsPlaybackPluginInfo.pluginContainerElement.classList.add('reactions-playback-wrapper');
               setReactionsPlaybackDomElement(reactionsPlaybackPluginInfo.pluginContainerElement);
            }

            const commentMarkerPluginInfo = IsVideo && await mediaInterface.registerPlugin('knowmiaCommentMarkers', Constants.pluginElements.progressBar);
            const annotationPluginInfo = await mediaInterface.registerPlugin('knowmiaAnnotation', Constants.pluginElements.media);
            IsVideo && commentMarkerPluginInfo.pluginContainerElement.classList.add('comment-track');
            annotationPluginInfo.pluginContainerElement.classList.add('annotation-wrapper');
            setCommentMarkerDomElement(commentMarkerPluginInfo.pluginContainerElement);
            setAnnotationDomElement(annotationPluginInfo.pluginContainerElement);

            if (isEmbedded && showEmbeddedSnagitMarketing()) {
               const snagitMarketingPluginInfo = await mediaInterface.registerPlugin('knowmiaSnagitMarketing', Constants.pluginElements.player);
               setSnagitMarketingMediaDomElement(snagitMarketingPluginInfo.pluginContainerElement);
            }

            const captionDisplayStatusPluginInfo = IsVideo && await mediaInterface.registerPlugin('TechSmithCaptionDisplayStatus', Constants.pluginElements.player);
            IsVideo && captionDisplayStatusPluginInfo.pluginContainerElement.classList.add('caption-display-status');
            setCaptionDisplayStatusDomElement(captionDisplayStatusPluginInfo.pluginContainerElement);
         }
      }
   };

   const appendSmartPlayerApp = () => {
      if (smartPlayerContainer.current && !smartPlayerContainer.current.contains(smartPlayerApp)) {
         smartPlayerContainer.current.appendChild(smartPlayerApp);
      }
   };

   const onSnagitMarketingPluginClose = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      setUserHasHiddenSnagitMarketing(true);
   };

   const displaySnagitMarketingOverMedia = (): boolean => showSnagitMarketing && !userHasHiddenSnagitMarketing && !!snagitMarketingMediaDomElement;

   useEffect(() => {
      if (!userProfileIsLoading) {
         setUserInfo();
      }
   }, [userProfileIsLoading]);

   useEffect(() => {
      mediaInterface.setTheme(theme);
   }, [theme]);

   useEffect(() => {
      const handleHideAnnotationsAndSelectComment = () => {
         const roundedTime = Math.floor(mediaInterface.currentTime);
         const commentToSelect = comments?.find(comment => Math.floor(comment.time) === roundedTime);

         if (Math.floor(SelectedComment?.time) !== Math.floor(commentToSelect?.time) && commentToSelect) {
            updateSelectedConversationState({SelectedComment: commentToSelect});
         }

         if (annotationMode === AnnotationModes.display) {
            const timeDifference = TimeFormatter.getRoundedTime(Math.abs(mediaInterface.currentTime - SelectedComment?.time));
            const timeMismatch = timeDifference > Constants.annotation.timeTolerance;

            if (timeMismatch) {
               updateAnnotationManagerState({annotationMode: AnnotationModes.none});
            }
         }
      };

      mediaInterface.addEventListener<ISmartPlayerTimeUpdateEvent>(events.ExternalVideo.VideoTimeUpdate, handleHideAnnotationsAndSelectComment);

      return () => {
         mediaInterface.removeEventListener(events.ExternalVideo.VideoTimeUpdate, handleHideAnnotationsAndSelectComment);
      };
   }, [SelectedComment, annotationMode, comments]);

   useEffect(() => {
      mediaInterface.doubleClickControlEnabled = annotationMode !== AnnotationModes.edit;
   }, [annotationMode]);

   useEffect(() => {
      if (smartPlayerMediaError) {
         void updateMediaStateFromServer();
         setSmartPlayerMediaError(false);
      }
   }, [smartPlayerMediaError]);

   useEffect(() => {
      if (ContentUrl) {
         if (started) {
            mediaInterface.destroy();
            started = false;
         }
         configure();
         appendSmartPlayerApp();
         void run();
      }
   }, [ContentUrl]);

   return (
      <SmartPlayerWrapper isEmbedded={isEmbedded}>
         <SmartPlayerContainerDiv data-hj-suppress className={'smart-player-container'} ref={smartPlayerContainer} annotationMode={annotationMode} isEmbedView={isEmbedded} isImage={IsImage} isYouTube={IsYouTube} isQuizUserSelfIdentifyOpen={quizUserSelfIdentifyIsOpen} />
         {commentMarkerDomElement && ReactDOM.createPortal(<CommentMarkers mediaDuration={mediaDuration} />, commentMarkerDomElement as Element)}
         {reactionsDomElement && ReactDOM.createPortal(<ReactionsPlugin mediaDuration={mediaDuration} />, reactionsDomElement as Element)}
         {reactionsPlaybackDomElement && ReactDOM.createPortal(<ReactionsPlaybackPlugin mediaDuration={mediaDuration} />, reactionsPlaybackDomElement as Element)}
         {annotationDomElement && ReactDOM.createPortal(<Annotation />, annotationDomElement as Element)}
         {CaptioningType === CaptioningTypes.AutoTranscribed && captionDisplayStatusDomElement && ReactDOM.createPortal(<CaptionDisplayStatusPlugin />, captionDisplayStatusDomElement as Element)}
         {displaySnagitMarketingOverMedia() && ReactDOM.createPortal(<SnagitMarketingEmbedPlugin canClose={true} linkToMoreInfo={embedPlatform !== EmbedPlatforms.slack} onClose={onSnagitMarketingPluginClose} />, snagitMarketingMediaDomElement as Element)}
      </SmartPlayerWrapper>
   );
};

export interface ISmartPlayerProps {
   isEmbedded: boolean;
   embedPlatform?: EmbedPlatforms;
   onDisplayModeChange?: (displayMode: string) => void;
}

export interface IStateMappedProps {
   annotationMode: AnnotationModes;
   SelectedComment: IComment;
   comments: IComment[];
   theme: TscThemeNames;
   ContentUrl: string;
   Hash: string;
   IsImage: boolean;
   IsVideo: boolean;
   IsYouTube: boolean;
   Title: string;
   Description: string;
   HlsTokenInjectionProxyContentUrl: string;
   PosterUrl: string;
   SpriteUrl: string;
   AllowViewerToFastForwardVideo: boolean;
   XmpUrl: string;
   AudioDescriptionUrl: string;
   QuizId: string;
   Rotate: number;
   PreviewThumbnailsUri: string;
   PreviewThumbnailsInterval: number;
   PreviewThumbnailsMaxColumns: number;
   PreviewThumbnailsThumbHeight: number;
   PreviewThumbnailsThumbWidth: number;
   CaptioningType: CaptioningTypes;
   Chapters?: IMediaChapters;
}

const mapStatesToProps = (
   {isEmbedded, embedPlatform, onDisplayModeChange}: ISmartPlayerProps,
   {annotationMode}: Partial<IStateMappedProps>,
   {SelectedComment}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>,
   {ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters}: Partial<IStateMappedProps>
): IStateMappedProps & ISmartPlayerProps => ({isEmbedded, embedPlatform, onDisplayModeChange, annotationMode, SelectedComment, comments, theme, ContentUrl, Hash, IsImage, IsVideo, IsYouTube, Title, Description, HlsTokenInjectionProxyContentUrl, PosterUrl, SpriteUrl, AllowViewerToFastForwardVideo, XmpUrl, AudioDescriptionUrl, QuizId, Rotate, PreviewThumbnailsUri, PreviewThumbnailsInterval, PreviewThumbnailsMaxColumns, PreviewThumbnailsThumbHeight, PreviewThumbnailsThumbWidth, CaptioningType, Chapters});

export default withMemoizedContexts(mapStatesToProps, annotationManagerStore, selectedConversationStore, commentsStore, themeStore, mediaStore)(SmartPlayerBase);
